import {
  Amounts,
  assertUnreachable,
  CancellationToken,
  HttpStatusCode,
  InternationalizationAPI,
  RoundingMode,
  TalerBankConversionApi,
  TalerCorebankApi,
  TalerError,
} from "@gnu-taler/taler-util";
import {
  Attention,
  AsyncButton,
  InputText,
  InputToggle,
  Loading,
  RenderAmount,
  RouteDefinition,
  ShowInputErrorLabel,
  useBankCoreApiContext,
  useNotificationContext,
  useNotifiedOperation,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, h, VNode } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import {
  AccessToken,
  AmountJson,
  opFixedSuccess,
  TalerErrorCode,
} from "@gnu-taler/taler-util";
import { dummyHttpResponse } from "@gnu-taler/taler-util/http";
import {
  FormErrors,
  FormStatus,
  FormValues,
  useFormState,
} from "../hooks/form.js";
import {
  revalidateConversionInfo,
  revalidateConversionRateClassDetails,
  revalidateConversionRateClassUsers,
  TransferCalculation,
  useCashinEstimatorForClass,
  useCashoutEstimatorForClass,
  useConversionInfo,
  useConversionRateClassDetails,
  useConversionRateClassUsers,
} from "../hooks/regional.js";
import { useSessionState } from "../hooks/session.js";
import { RecursivePartial, undefinedIfEmpty } from "../utils.js";
import { DescribeConversion } from "./admin/ConversionClassList.js";
import { InputAmount } from "./PaytoWireTransferForm.js";
import { ConversionForm } from "./regional/ConversionConfig.js";
import { RetryableError } from "../components/RetryableError.js";
import { OperationError } from "../components/OperationError.js";

interface Props {
  classId: number;
  routeCancel: RouteDefinition;
  onClassDeleted: () => void;
}

type FormType = {
  name: string;
  description: string;
  conv: Omit<
    Omit<TalerBankConversionApi.ConversionRate, "cashout_tiny_amount">,
    "cashin_tiny_amount"
  >;
};

export function ConversionRateClassDetails({
  routeCancel,
  classId,
  onClassDeleted,
}: Props): VNode {
  const { i18n } = useTranslationContext();

  const detailsResult = useConversionRateClassDetails(classId);
  const conversionInfoResult = useConversionInfo();

  if (!detailsResult || !conversionInfoResult) {
    return <Loading />;
  }
  if (detailsResult instanceof TalerError) {
    return (
      <RetryableError
        error={detailsResult}
        title={i18n.str`Failed to load conversion details.`}
        onRetry={() => revalidateConversionRateClassDetails()}
      />
    );
  }
  if (conversionInfoResult instanceof TalerError) {
    return (
      <RetryableError
        error={conversionInfoResult}
        title={i18n.str`Failed to load conversion rate information.`}
        onRetry={() => revalidateConversionInfo()}
      />
    );
  }
  if (conversionInfoResult.type === "fail") {
    return (
      <Attention type="danger" title={i18n.str`Conversion is disabled`}>
        <i18n.Translate>
          Conversion should be enabled in the configuration, the conversion rate
          should be initialized with fee(s), rates and a rounding mode.
        </i18n.Translate>
      </Attention>
    );
  }
  if (detailsResult.type === "fail") {
    switch (detailsResult.case) {
      case HttpStatusCode.Unauthorized:
      case HttpStatusCode.Forbidden:
      case HttpStatusCode.NotFound:
      case HttpStatusCode.NotImplemented:
        return (
          <Attention type="danger" title={i18n.str`Conversion is disabled`}>
            <i18n.Translate>
              Conversion should be enabled in the configuration, the conversion
              rate should be initialized with fee(s), rates and a rounding mode.
            </i18n.Translate>
          </Attention>
        );
      default:
        assertUnreachable(detailsResult);
    }
  }
  return (
    <Form
      conversionInfo={conversionInfoResult.body}
      detailsResult={detailsResult.body}
      routeCancel={routeCancel}
      classId={classId}
      onClassDeleted={onClassDeleted}
    />
  );
}

function Form({
  conversionInfo,
  detailsResult,
  routeCancel,
  classId,
  onClassDeleted,
}: {
  conversionInfo: TalerBankConversionApi.TalerConversionInfoConfig;
  detailsResult: TalerCorebankApi.ConversionRateClass;
  routeCancel: RouteDefinition;
  classId: number;
  onClassDeleted: () => void;
}) {
  const { i18n } = useTranslationContext();
  // Translators: Incoming conversion from the regional currency into the
  // bank's fiat currency.
  const cashinLabel = i18n.str`Cashin`;
  // Translators: Outgoing conversion from the bank's fiat currency into the
  // regional currency.
  const cashoutLabel = i18n.str`Cashout`;
  const { state: credentials } = useSessionState();
  const creds = credentials.status !== "loggedIn" ? undefined : credentials;
  const { lib } = useBankCoreApiContext();
  const { showError } = useNotificationContext();
  const [section, setSection] = useState<
    "detail" | "cashout" | "cashin" | "users" | "test" | "delete"
  >("detail");

  const initalState: FormValues<FormType> = {
    name: detailsResult.name,
    description: detailsResult.description,
    conv: {
      cashin_min_amount: detailsResult.cashin_min_amount?.split(":")[1],
      cashin_fee: detailsResult.cashin_fee?.split(":")[1],
      cashin_ratio: detailsResult?.cashin_ratio,
      cashin_rounding_mode: detailsResult?.cashin_rounding_mode,
      cashout_min_amount: detailsResult.cashout_min_amount?.split(":")[1],
      cashout_fee: detailsResult.cashout_fee?.split(":")[1],
      cashout_ratio: detailsResult.cashout_ratio,
      cashout_rounding_mode: detailsResult.cashout_rounding_mode,
    },
  };

  const [form, status] = useFormState<FormType>(
    initalState,
    createFormValidator(
      i18n,
      conversionInfo.regional_currency,
      conversionInfo.fiat_currency,
    ),
  );

  // i18n.str`delete conversion rate class`,
  const deleteClass = useNotifiedOperation<
    Awaited<ReturnType<typeof lib.bank.deleteConversionRateClass>>,
    [AccessToken]
  >(
    (ct, token: AccessToken) =>
      lib.bank.deleteConversionRateClass(token, classId),
    {
      onSuccess: onClassDeleted,
      onFail: showError(
        i18n.str`Failed to delete conversion rate class`,
        (fail) => {
          switch (fail.case) {
            case HttpStatusCode.Unauthorized:
              return i18n.str`Unauthorized`;
            case HttpStatusCode.Forbidden:
              return i18n.str`Forbidden`;
            case HttpStatusCode.NotFound:
              return i18n.str`Conversion rate class was not found.`;
            case HttpStatusCode.NotImplemented:
              return i18n.str`Deleting conversion rate classes is not supported by this bank.`;
            default:
              assertUnreachable(fail);
          }
        },
      ),
    },
  );

  // i18n.str`update conversion rate class`,
  const updateClass = useNotifiedOperation<
    Awaited<ReturnType<typeof lib.bank.updateConversionRateClass>>,
    [AccessToken, number, TalerCorebankApi.ConversionRateClassInput]
  >(
    (
      ct,
      t: AccessToken,
      id: number,
      i: TalerCorebankApi.ConversionRateClassInput,
    ) => lib.bank.updateConversionRateClass(t, id, i),
    {
      onSuccess: () => {
        setSection("detail");
      },
      onFail: showError(
        i18n.str`Failed to update conversion rate class.`,
        (fail) => {
          switch (fail.case) {
            case HttpStatusCode.Unauthorized:
              return i18n.str`Unauthorized`;
            case HttpStatusCode.Forbidden:
              return i18n.str`Forbidden`;
            case HttpStatusCode.NotFound:
              return i18n.str`Not Found`;
            case HttpStatusCode.NotImplemented:
              return i18n.str`Not implemented`;
            case TalerErrorCode.BANK_NAME_REUSE:
              return i18n.str`The name of the conversion is already used.`;
            default:
              assertUnreachable(fail);
          }
        },
      ),
    },
  );

  const updateRequest: TalerCorebankApi.ConversionRateClassInput | undefined =
    status.status === "fail"
      ? undefined
      : {
          name: status.result.name,
          description: status.result.description,

          cashin_fee: status.result.conv.cashin_fee,
          cashin_min_amount: status.result.conv.cashin_min_amount,
          cashin_ratio: status.result.conv.cashin_ratio,
          cashin_rounding_mode: status.result.conv.cashin_rounding_mode,

          cashout_fee: status.result.conv.cashout_fee,
          cashout_min_amount: status.result.conv.cashout_min_amount,
          cashout_ratio: status.result.conv.cashout_ratio,
          cashout_rounding_mode: status.result.conv.cashout_rounding_mode,
        };

  const updateDetailsDisabled = !!(
    !creds ||
    !updateRequest ||
    section !== "detail" ||
    status.errors?.name ||
    status.errors?.description ||
    (status.result.name === initalState.name &&
      status.result.description === initalState.description)
  );

  const updateCashinDisabled = !!(
    !creds ||
    !updateRequest ||
    section !== "cashin" ||
    status.errors?.conv?.cashin_fee ||
    status.errors?.conv?.cashin_min_amount ||
    status.errors?.conv?.cashin_ratio ||
    status.errors?.conv?.cashin_rounding_mode
  );

  const updateCashoutDisabled = !!(
    !creds ||
    !updateRequest ||
    section !== "cashout" ||
    // no errors on fields
    status.errors?.conv?.cashout_fee ||
    status.errors?.conv?.cashout_min_amount ||
    status.errors?.conv?.cashout_ratio ||
    status.errors?.conv?.cashout_rounding_mode ||
    // at least on field changed
    (status.result?.conv?.cashout_fee === initalState.conv.cashout_fee &&
      status.result?.conv?.cashout_min_amount ===
        initalState.conv.cashout_min_amount &&
      status.result?.conv?.cashout_ratio === initalState.conv.cashout_ratio &&
      status.result?.conv?.cashout_rounding_mode ===
        initalState.conv.cashout_rounding_mode)
  );

  const default_rate = conversionInfo.conversion_rate;

  const final_cashin_ratio =
    detailsResult.cashin_ratio ?? default_rate.cashin_ratio;
  const final_cashin_fee = detailsResult.cashin_fee ?? default_rate.cashin_fee;
  const final_cashin_min =
    detailsResult.cashin_min_amount ?? default_rate.cashin_min_amount;
  const final_cashin_rounding =
    detailsResult.cashin_rounding_mode ?? default_rate.cashin_rounding_mode;

  const final_cashout_ratio =
    detailsResult.cashout_ratio ?? default_rate.cashout_ratio;
  const final_cashout_fee =
    detailsResult.cashout_fee ?? default_rate.cashout_fee;
  const final_cashout_min =
    detailsResult.cashout_min_amount ?? default_rate.cashout_min_amount;
  const final_cashout_rounding =
    detailsResult.cashout_rounding_mode ?? default_rate.cashout_rounding_mode;

  const in_ratio = Number.parseFloat(final_cashin_ratio);
  const out_ratio = Number.parseFloat(final_cashout_ratio);

  const both_high = in_ratio > 1 && out_ratio > 1;
  const both_low = in_ratio < 1 && out_ratio < 1;

  const sections = [
    { id: "detail", label: i18n.str`Details` },
    { id: "cashout", label: i18n.str`Cashout settings` },
    { id: "cashin", label: i18n.str`Cash-in settings` },
    { id: "users", label: i18n.str`Accounts` },
    { id: "test", label: i18n.str`Test conversion` },
    { id: "delete", label: i18n.str`Delete class` },
  ] as const;

  return (
    <section class="mt-2">
      <a
        href={routeCancel.url({})}
        class="inline-flex items-center text-sm font-semibold text-brand hover:underline"
      >
        <span class="mr-1" aria-hidden="true">
          ←
        </span>
        <i18n.Translate>Back to conversion settings</i18n.Translate>
      </a>
      <div class="mx-auto mt-6 max-w-4xl">
        <h1 class="text-2xl font-semibold text-brand">{detailsResult.name}</h1>
        <p class="mt-2 text-sm text-gray-600">
          <i18n.Translate>
            Manage this conversion rate class and the accounts assigned to it.
          </i18n.Translate>
        </p>

        <nav
          aria-label={i18n.str`Conversion rate class sections`}
          class="mt-6 flex flex-wrap gap-2"
        >
          {sections.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-current={section === item.id ? "page" : undefined}
              onClick={() => setSection(item.id)}
              class={`rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${
                section === item.id
                  ? item.id === "delete"
                    ? "border-red-700 bg-red-50 text-red-800"
                    : "border-primary bg-primary text-onPrimary"
                  : item.id === "delete"
                    ? "border-red-200 bg-white text-red-700 hover:bg-red-50"
                    : "border-gray-300 bg-white text-onBackground hover:border-primary hover:text-primary"
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <form
          class="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
          autoCapitalize="none"
          autoCorrect="off"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          {section == "cashin" && (
            <ConversionForm
              id="cashin"
              inputCurrency={conversionInfo.fiat_currency}
              outputCurrency={conversionInfo.regional_currency}
              fee={form?.conv?.cashin_fee}
              minimum={form?.conv?.cashin_min_amount}
              ratio={form?.conv?.cashin_ratio}
              rounding={form?.conv?.cashin_rounding_mode}
              tiny={undefined}
              fallback_fee={default_rate.cashin_fee.split(":")[1]}
              fallback_minimum={default_rate.cashin_min_amount.split(":")[1]}
              fallback_ratio={default_rate.cashin_ratio}
              fallback_rounding={default_rate.cashin_rounding_mode}
              fallback_tiny={default_rate.cashin_tiny_amount}
            />
          )}

          {section == "cashout" && (
            <Fragment>
              <ConversionForm
                id="cashout"
                inputCurrency={conversionInfo.regional_currency}
                outputCurrency={conversionInfo.fiat_currency}
                fee={form?.conv?.cashout_fee}
                minimum={form?.conv?.cashout_min_amount}
                ratio={form?.conv?.cashout_ratio}
                rounding={form?.conv?.cashout_rounding_mode}
                tiny={undefined}
                fallback_fee={default_rate.cashout_fee.split(":")[1]}
                fallback_minimum={default_rate.cashout_min_amount.split(":")[1]}
                fallback_ratio={default_rate.cashout_ratio}
                fallback_rounding={default_rate.cashout_rounding_mode}
                fallback_tiny={default_rate.cashout_tiny_amount}
              />
            </Fragment>
          )}

          {section == "detail" && (
            <Fragment>
              <div class="space-y-6 px-5 py-6 sm:px-8">
                <div>
                  <label
                    class="block text-sm font-medium text-onBackground"
                    htmlFor="name"
                  >
                    <i18n.Translate>Name</i18n.Translate>
                  </label>
                  <div class="mt-2">
                    <input
                      type="text"
                      name="name"
                      id="name"
                      class="block w-full disabled:bg-gray-200 rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                      value={form?.name?.value ?? ""}
                      enterkeyhint="next"
                      placeholder={i18n.str`identification`}
                      autocomplete="off"
                      title={i18n.str`Conversion rate class name`}
                      required
                      onInput={(e): void => {
                        form?.name?.onUpdate(e.currentTarget.value);
                      }}
                    />
                    <ShowInputErrorLabel
                      message={form?.name?.error}
                      isDirty={form?.name?.value !== undefined}
                    />
                  </div>
                </div>

                <div>
                  <label
                    class="block text-sm font-medium text-onBackground"
                    htmlFor="description"
                  >
                    <i18n.Translate>Description</i18n.Translate>
                  </label>
                  <div class="mt-2">
                    <input
                      type="text"
                      name="description"
                      id="description"
                      class="block w-full disabled:bg-gray-200 rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                      value={form?.description?.value ?? ""}
                      enterkeyhint="next"
                      // placeholder="identification"
                      autocomplete="off"
                      title={i18n.str`Conversion rate class description`}
                      onInput={(e): void => {
                        form?.description?.onUpdate(e.currentTarget.value);
                      }}
                    />
                    <ShowInputErrorLabel
                      message={form?.description?.error}
                      isDirty={form?.description?.value !== undefined}
                    />
                  </div>
                </div>

                <dl class="divide-y divide-gray-100 rounded-lg border border-gray-200 px-4">
                  <div class="py-4 sm:grid sm:grid-cols-[8rem_1fr] sm:gap-4">
                    <dt class="text-sm font-medium text-gray-600">
                      {cashinLabel}
                    </dt>
                    <dd class="mt-1 text-sm text-onBackground sm:mt-0">
                      <DescribeConversion
                        ratio={final_cashin_ratio}
                        fee={final_cashin_fee}
                        min={final_cashin_min}
                        rounding={final_cashin_rounding}
                        minSpec={conversionInfo.fiat_currency_specification}
                        feeSpec={conversionInfo.regional_currency_specification}
                      />
                    </dd>
                  </div>
                  <div class="py-4 sm:grid sm:grid-cols-[8rem_1fr] sm:gap-4">
                    <dt class="text-sm font-medium text-gray-600">
                      {cashoutLabel}
                    </dt>
                    <dd class="mt-1 text-sm text-onBackground sm:mt-0">
                      <DescribeConversion
                        ratio={final_cashout_ratio}
                        fee={final_cashout_fee}
                        min={final_cashout_min}
                        rounding={final_cashout_rounding}
                        minSpec={conversionInfo.regional_currency_specification}
                        feeSpec={conversionInfo.fiat_currency_specification}
                      />
                    </dd>
                  </div>
                  <div class="py-4 sm:grid sm:grid-cols-[8rem_1fr] sm:gap-4">
                    <dt class="text-sm font-medium text-gray-600">
                      <i18n.Translate>Users</i18n.Translate>
                    </dt>
                    <dd class="mt-1 text-sm text-onBackground sm:mt-0">
                      {detailsResult.num_users}
                    </dd>
                  </div>
                </dl>

                {both_low || both_high ? (
                  <Attention title={i18n.str`Bad ratios`} type="warning">
                    <i18n.Translate>
                      One ratio must be greater than or equal to 1, and the
                      other must be less than or equal to 1.
                    </i18n.Translate>
                  </Attention>
                ) : undefined}
              </div>
            </Fragment>
          )}

          {section == "users" && (
            <AccountsOnConversionClass classId={classId} />
          )}
          {section == "delete" && (
            <DeleteConversionClass
              classId={classId}
              userCount={detailsResult.num_users}
            />
          )}

          {section == "test" && (
            <TestConversionClass classId={classId} info={conversionInfo} />
          )}

          <div class="border-t border-onBackground/10 bg-background/60 px-5 py-4 sm:px-8">
            <OperationError class="mb-4" />
            <div class="flex items-center justify-between gap-x-6">
              <a
                name="cancel"
                href={routeCancel.url({})}
                class="text-sm font-semibold leading-6 text-onBackground"
              >
                <i18n.Translate>Cancel</i18n.Translate>
              </a>
              {section == "cashin" ? (
                <Fragment>
                  <AsyncButton
                    submit
                    name="update conversion"
                    class="disabled:opacity-50 disabled:cursor-default cursor-pointer rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    disabled={updateCashinDisabled}
                    onClick={() =>
                      updateClass.run(creds!.token, classId, updateRequest!)
                    }
                  >
                    <i18n.Translate>Update</i18n.Translate>
                  </AsyncButton>
                </Fragment>
              ) : undefined}
              {section == "cashout" ? (
                <Fragment>
                  <AsyncButton
                    submit
                    name="update conversion"
                    class="disabled:opacity-50 disabled:cursor-default cursor-pointer rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    disabled={updateCashoutDisabled}
                    onClick={() =>
                      updateClass.run(creds!.token, classId, updateRequest!)
                    }
                  >
                    <i18n.Translate>Update</i18n.Translate>
                  </AsyncButton>
                </Fragment>
              ) : undefined}
              {section == "detail" ? (
                <Fragment>
                  <AsyncButton
                    submit
                    name="update conversion"
                    class="disabled:opacity-50 disabled:cursor-default cursor-pointer rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    disabled={updateDetailsDisabled}
                    onClick={() =>
                      updateClass.run(creds!.token, classId, updateRequest!)
                    }
                  >
                    <i18n.Translate>Update</i18n.Translate>
                  </AsyncButton>
                </Fragment>
              ) : undefined}
              {section == "delete" ? (
                <Fragment>
                  <AsyncButton
                    submit
                    name="update conversion"
                    class="disabled:opacity-50 disabled:cursor-default cursor-pointer rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                    disabled={
                      !creds ||
                      section !== "delete" ||
                      detailsResult.num_users > 0
                    }
                    onClick={() => deleteClass.run(creds!.token)}
                  >
                    <i18n.Translate>Delete</i18n.Translate>
                  </AsyncButton>
                </Fragment>
              ) : undefined}
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}

export function createFormValidator(
  i18n: InternationalizationAPI,
  regional: string,
  fiat: string,
) {
  return function check(state: FormValues<FormType>): FormStatus<FormType> {
    const cashin_min_amount = Amounts.parse(
      `${fiat}:${state.conv.cashin_min_amount}`,
    );

    const cashin_fee = Amounts.parse(`${regional}:${state.conv.cashin_fee}`);

    const cashout_min_amount = Amounts.parse(
      `${regional}:${state.conv.cashout_min_amount}`,
    );
    const cashout_fee = Amounts.parse(`${fiat}:${state.conv.cashout_fee}`);

    const cashinRatioText = state.conv.cashin_ratio?.trim() ?? "";
    const cashoutRatioText = state.conv.cashout_ratio?.trim() ?? "";
    const cashin_ratio = Number(cashinRatioText);
    const cashout_ratio = Number(cashoutRatioText);
    const cashinRatioValid =
      !cashinRatioText || (Number.isFinite(cashin_ratio) && cashin_ratio > 0);
    const cashoutRatioValid =
      !cashoutRatioText ||
      (Number.isFinite(cashout_ratio) && cashout_ratio > 0);

    const errors = undefinedIfEmpty<FormErrors<FormType>>({
      conv: undefinedIfEmpty<FormErrors<FormType["conv"]>>({
        cashin_min_amount: !state.conv.cashin_min_amount
          ? undefined
          : !cashin_min_amount
            ? i18n.str`Invalid`
            : undefined,
        cashin_fee: !state.conv.cashin_fee
          ? undefined
          : !cashin_fee
            ? i18n.str`Invalid`
            : undefined,

        cashout_min_amount: !state.conv.cashout_min_amount
          ? undefined
          : !cashout_min_amount
            ? i18n.str`Invalid`
            : undefined,
        cashout_fee: !state.conv.cashout_fee
          ? undefined
          : !cashout_fee
            ? i18n.str`Invalid`
            : undefined,

        cashin_rounding_mode: !state.conv.cashin_rounding_mode
          ? undefined
          : undefined,
        cashout_rounding_mode: !state.conv.cashout_rounding_mode
          ? undefined
          : undefined,

        cashin_ratio: cashinRatioValid ? undefined : i18n.str`Invalid`,
        cashout_ratio: cashoutRatioValid ? undefined : i18n.str`Invalid`,
      }),

      description: undefined,
      name: !state.name ? i18n.str`Required` : undefined,
    });

    const result: RecursivePartial<FormType> = {
      name: !errors?.name ? state.name : undefined,
      description: state.description,
      conv: {
        cashin_fee:
          !errors?.conv?.cashin_fee && cashin_fee
            ? Amounts.stringify(cashin_fee)
            : undefined,
        cashin_min_amount:
          !errors?.conv?.cashin_min_amount && cashin_min_amount
            ? Amounts.stringify(cashin_min_amount)
            : undefined,
        cashin_ratio:
          !errors?.conv?.cashin_ratio && cashinRatioText
            ? String(cashin_ratio)
            : undefined,
        cashin_rounding_mode: !errors?.conv?.cashin_rounding_mode
          ? (state.conv.cashin_rounding_mode! as RoundingMode)
          : undefined,
        cashout_fee:
          !errors?.conv?.cashout_fee && cashout_fee
            ? Amounts.stringify(cashout_fee)
            : undefined,
        cashout_min_amount:
          !errors?.conv?.cashout_min_amount && cashout_min_amount
            ? Amounts.stringify(cashout_min_amount)
            : undefined,
        cashout_ratio:
          !errors?.conv?.cashout_ratio && cashoutRatioText
            ? String(cashout_ratio)
            : undefined,
        cashout_rounding_mode: !errors?.conv?.cashout_rounding_mode
          ? (state.conv.cashout_rounding_mode! as RoundingMode)
          : undefined,
      },
    };
    return errors === undefined
      ? { status: "ok", result: result as FormType, errors }
      : { status: "fail", result: result as FormType, errors };
  };
}

function TestConversionClass({
  classId,
  info,
}: {
  classId: number;
  info: TalerBankConversionApi.TalerConversionInfoConfig;
}): VNode {
  const { i18n } = useTranslationContext();
  // Translators: Label for the amount after currency conversion.
  const convertedLabel = i18n.str`Converted`;
  const { showError } = useNotificationContext();

  const { estimateByDebit: calculateCashoutFromDebit } =
    useCashoutEstimatorForClass(classId);
  const { estimateByDebit: calculateCashinFromDebit } =
    useCashinEstimatorForClass(classId);

  const [amount, setAmount] = useState<string>("100");
  const [calculationResult, setCalc] = useState<{
    key: string;
    cashin: TransferCalculation;
    cashout: TransferCalculation;
  }>();

  const calculationKey = amount.trim();
  const in_amount = Amounts.parse(`${info.fiat_currency}:${calculationKey}`);
  const error = !calculationKey
    ? i18n.str`Required`
    : !in_amount
      ? i18n.str`Invalid`
      : undefined;

  const in_fee = Amounts.parseOrThrow(info.conversion_rate.cashin_fee);
  const out_fee = Amounts.parseOrThrow(info.conversion_rate.cashout_fee);

  async function calculateOperation(
    _ct: CancellationToken,
    key: string,
    amount: AmountJson,
  ) {
    const respCashin = await calculateCashinFromDebit(amount, in_fee);
    if (respCashin.type === "fail") {
      return respCashin;
    }
    const cashin = respCashin.body;
    const respCashout = await calculateCashoutFromDebit(cashin.credit, out_fee);
    if (respCashout.type === "fail") {
      return respCashout;
    }
    const cashout = respCashout.body;
    return opFixedSuccess(dummyHttpResponse, { key, cashin, cashout });
  }

  // i18n.str`calculate cashout fee`,
  const calculate = useNotifiedOperation<
    Awaited<ReturnType<typeof calculateOperation>>,
    [string, AmountJson]
  >(calculateOperation, {
    onSuccess: (resp) => setCalc(resp),
    onFail: showError(
      i18n.str`Failed to calculate the cashout fee.`,
      (fail) => {
        switch (fail.case) {
          case HttpStatusCode.Conflict:
            return i18n.str`The amount is too small`;
          case HttpStatusCode.NotImplemented:
            return i18n.str`Conversion is not implemented.`;
          case TalerErrorCode.GENERIC_PARAMETER_MISSING:
            return i18n.str`At least a debit or credit amount must be provided.`;
          case TalerErrorCode.GENERIC_PARAMETER_MALFORMED:
            return i18n.str`The amount has an invalid format.`;
          case TalerErrorCode.GENERIC_CURRENCY_MISMATCH:
            return i18n.str`The currency is not supported`;
          default:
            assertUnreachable(fail);
        }
      },
    ),
  });
  const calculateRunning = calculate.running;
  const calculateRun = calculate.run;
  const calculateCancel = calculate.cancel;
  const calculationStartedFor = useRef<string>();

  useEffect(() => {
    calculationStartedFor.current = undefined;
    setCalc(undefined);
    calculateCancel();
  }, [calculateCancel, calculationKey]);

  useEffect(() => {
    if (
      !calculateRunning &&
      calculationStartedFor.current !== calculationKey &&
      in_amount &&
      !error
    ) {
      calculationStartedFor.current = calculationKey;
      void calculateRun(calculationKey, in_amount);
    }
  }, [calculateRun, calculateRunning, calculationKey, error, in_amount]);

  const currentCalculation =
    calculationResult?.key === calculationKey ? calculationResult : undefined;
  const cashinCalc = currentCalculation?.cashin;
  const cashoutCalc = currentCalculation?.cashout;

  return (
    <Fragment>
      <div class="px-6 pt-6">
        <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
          <div class="sm:col-span-5">
            <label
              for="amount"
              class="block text-sm font-medium leading-6 text-onBackground"
            >{i18n.str`Initial amount`}</label>
            <InputAmount
              name="amount"
              left
              currency={info.fiat_currency}
              value={amount ?? ""}
              onChange={(d) => {
                setAmount(d);
              }}
            />
            <ShowInputErrorLabel
              message={error}
              isDirty={amount !== undefined}
            />
            <p class="mt-2 text-sm text-gray-500">
              <i18n.Translate>
                Use it to test how the conversion will affect the amount.
              </i18n.Translate>
            </p>
          </div>
        </div>
      </div>

      {!cashoutCalc || !cashinCalc ? undefined : (
        <div class="px-6 pt-6">
          <div class="sm:col-span-5">
            <dl class="mt-4 space-y-4">
              <div class="justify-between items-center flex ">
                <dt class="text-sm text-gray-600">
                  <i18n.Translate>Sending to this bank</i18n.Translate>
                </dt>
                <dd class="text-sm text-onBackground">
                  <RenderAmount
                    value={cashinCalc.debit}
                    negative
                    withColor
                    spec={info.fiat_currency_specification}
                  />
                </dd>
              </div>

              {Amounts.isZero(cashinCalc.beforeFee) ? undefined : (
                <div class="flex items-center justify-between afu ">
                  <dt class="flex items-center text-sm text-gray-600">
                    <span>{convertedLabel}</span>
                  </dt>
                  <dd class="text-sm text-onBackground">
                    <RenderAmount
                      value={cashinCalc.beforeFee}
                      spec={info.regional_currency_specification}
                    />
                  </dd>
                </div>
              )}
              <div class="flex justify-between items-center border-t-2 afu pt-4">
                <dt class="text-lg text-onBackground font-medium">
                  <i18n.Translate>Cashin after fee</i18n.Translate>
                </dt>
                <dd class="text-lg text-onBackground font-medium">
                  <RenderAmount
                    value={cashinCalc.credit}
                    withColor
                    spec={info.regional_currency_specification}
                  />
                </dd>
              </div>
            </dl>
          </div>

          <div class="sm:col-span-5">
            <dl class="mt-4 space-y-4">
              <div class="justify-between items-center flex ">
                <dt class="text-sm text-gray-600">
                  <i18n.Translate>Sending from this bank</i18n.Translate>
                </dt>
                <dd class="text-sm text-onBackground">
                  <RenderAmount
                    value={cashoutCalc.debit}
                    negative
                    withColor
                    spec={info.regional_currency_specification}
                  />
                </dd>
              </div>

              {Amounts.isZero(cashoutCalc.beforeFee) ? undefined : (
                <div class="flex items-center justify-between afu">
                  <dt class="flex items-center text-sm text-gray-600">
                    <span>{convertedLabel}</span>
                  </dt>
                  <dd class="text-sm text-onBackground">
                    <RenderAmount
                      value={cashoutCalc.beforeFee}
                      spec={info.fiat_currency_specification}
                    />
                  </dd>
                </div>
              )}
              <div class="flex justify-between items-center border-t-2 afu pt-4">
                <dt class="text-lg text-onBackground font-medium">
                  <i18n.Translate>Cashout after fee</i18n.Translate>
                </dt>
                <dd class="text-lg text-onBackground font-medium">
                  <RenderAmount
                    value={cashoutCalc.credit}
                    withColor
                    spec={info.fiat_currency_specification}
                  />
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}
    </Fragment>
  );
}
function DeleteConversionClass({
  classId,
  userCount,
}: {
  classId: number;
  userCount: number;
}): VNode {
  const { i18n } = useTranslationContext();

  return (
    <Fragment>
      <div class="px-4 mt-4">
        {userCount > 0 ? (
          <Attention
            type="danger"
            title={i18n.str`Can't remove the conversion rate class`}
          >
            <i18n.Translate>
              There are some users associated with this class. You need to
              remove them first.
            </i18n.Translate>
          </Attention>
        ) : (
          <Attention
            type="warning"
            title={i18n.str`You are going to remove the conversion rate class`}
          >
            <i18n.Translate>This step can't be undone.</i18n.Translate>
          </Attention>
        )}
      </div>
    </Fragment>
  );
}

function AccountsOnConversionClass({ classId }: { classId: number }): VNode {
  const { i18n } = useTranslationContext();
  // Translators: Table column containing the assigned conversion-rate class.
  const classLabel = i18n.str`Class`;
  // Translators: Table column containing buttons for assigning or removing an
  // account.
  const actionLabel = i18n.str`Action`;

  const {
    lib: { bank },
  } = useBankCoreApiContext();
  const { showError } = useNotificationContext();
  const { state } = useSessionState();
  const resultInfo = useConversionInfo();
  const token = state.status === "loggedIn" ? state.token : undefined;

  const [filter, setFilter] = useState<{
    showAll?: boolean;
    classId?: number;
    account?: string;
  }>({
    showAll: classId === undefined,
    classId,
  });
  const userListResult = useConversionRateClassUsers(
    filter.classId,
    filter.account,
  );
  const membership = useNotifiedOperation<
    Awaited<ReturnType<typeof bank.updateAccount>>,
    [string, AccessToken, number | null]
  >(
    (_ct, username, accessToken, conversionRateClassId) =>
      bank.updateAccount(
        { username, token: accessToken },
        { conversion_rate_class_id: conversionRateClassId },
      ),
    {
      onSuccess: async () => {
        await Promise.all([
          revalidateConversionRateClassUsers(),
          revalidateConversionRateClassDetails(),
        ]);
      },
      onFail: showError(
        i18n.str`Failed to update conversion class membership.`,
        () => i18n.str`The account could not be updated. Please try again.`,
      ),
    },
  );
  if (!userListResult || !resultInfo) {
    return <Loading />;
  }
  if (resultInfo instanceof TalerError) {
    return (
      <RetryableError
        error={resultInfo}
        title={i18n.str`Failed to load conversion rate information.`}
        onRetry={() => revalidateConversionInfo()}
      />
    );
  }
  if (resultInfo.type === "fail") {
    return (
      <Attention type="danger" title={i18n.str`Conversion is disabled`}>
        <i18n.Translate>
          Conversion rate information is unavailable for these accounts.
        </i18n.Translate>
      </Attention>
    );
  }
  const convInfo = resultInfo.body;
  if (userListResult.tag === "error") {
    const failure = userListResult.error;
    if (failure instanceof TalerError) {
      return (
        <RetryableError
          error={failure}
          title={i18n.str`Failed to load users in conversion class.`}
          onRetry={() => revalidateConversionRateClassUsers()}
        />
      );
    }
    switch (failure.case) {
      case HttpStatusCode.Unauthorized:
        return (
          <Attention type="danger" title={i18n.str`Conversion is disabled`}>
            <i18n.Translate>
              Conversion should be enabled in the configuration, the conversion
              rate should be initialized with fee(s), rates and a rounding mode.
            </i18n.Translate>
          </Attention>
        );
      default:
        assertUnreachable(failure.case);
    }
  }
  const userListPage = userListResult.value;
  return (
    <Fragment>
      <div class="px-4 mt-4">
        <div class="sm:flex sm:items-center">
          <div class="sm:flex-auto">
            <h1 class="text-base font-semibold leading-6 text-onBackground">
              <i18n.Translate>Filters</i18n.Translate>
            </h1>
          </div>
        </div>
      </div>
      <div class="px-4 mt-2">
        <InputToggle
          label={i18n.str`Show from other classes`}
          name="show_all"
          threeState={false}
          handler={{
            field: {
              value: filter.showAll,
              onChange(v) {
                filter.showAll = !!v;
                if (!v) {
                  filter.classId = classId;
                } else {
                  filter.classId = undefined;
                }
                setFilter(structuredClone(filter));
              },
              name: "show_all",
            },
          }}
        />
        <InputText
          label={i18n.str`Account`}
          name="account"
          handler={{
            field: {
              value: filter.account,
              onChange(v) {
                filter.account = v;
                setFilter(structuredClone(filter));
              },
              name: "account",
            },
          }}
        />
        {filter.showAll ? (
          <InputText
            label={i18n.str`Group ID`}
            name="crcid"
            handler={{
              field: {
                value: String(filter.classId),
                onChange(v) {
                  const id = !v ? undefined : Number.parseInt(v, 10);
                  filter.classId = id;
                  setFilter(structuredClone(filter));
                },
                name: "crcid",
              },
            }}
          />
        ) : undefined}
      </div>
      <div class="mt-4 flow-root">
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            {!userListPage.items.length ? (
              <div class="py-3.5 pl-4 pr-3 ">
                <i18n.Translate>
                  No users in this conversion rate class
                </i18n.Translate>
              </div>
            ) : (
              <table class="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-onBackground"
                    >{i18n.str`Name`}</th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-onBackground"
                    >
                      {classLabel}
                    </th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-onBackground"
                    >{i18n.str`Cashin`}</th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-onBackground"
                    >{i18n.str`Cashout`}</th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-onBackground"
                    >
                      {actionLabel}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  {userListPage.items.map((item) => {
                    return (
                      <tr
                        key={item.username}
                        class="data-[status=deleted]:bg-gray-100"
                        data-status={item.status}
                      >
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {item.name}
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {item.conversion_rate_class_id}
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {item.conversion_rate ? (
                            <DescribeConversion
                              ratio={item.conversion_rate.cashin_ratio}
                              fee={item.conversion_rate.cashin_fee}
                              min={item.conversion_rate.cashin_min_amount}
                              rounding={
                                item.conversion_rate.cashin_rounding_mode
                              }
                              minSpec={convInfo.fiat_currency_specification}
                              feeSpec={convInfo.regional_currency_specification}
                            />
                          ) : (
                            <i18n.Translate>Not configured</i18n.Translate>
                          )}
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {item.conversion_rate ? (
                            <DescribeConversion
                              ratio={item.conversion_rate.cashout_ratio}
                              fee={item.conversion_rate.cashout_fee}
                              min={item.conversion_rate.cashout_min_amount}
                              rounding={
                                item.conversion_rate.cashout_rounding_mode
                              }
                              minSpec={convInfo.fiat_currency_specification}
                              feeSpec={convInfo.regional_currency_specification}
                            />
                          ) : (
                            <i18n.Translate>Not configured</i18n.Translate>
                          )}
                        </td>
                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {classId === item.conversion_rate_class_id ? (
                            <button
                              type="button"
                              disabled={membership.running || !token}
                              class="disabled:opacity-50 disabled:bg-gray-600 disabled:hover:bg-gray-600 disabled:cursor-default cursor-pointer rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                              onClick={() => {
                                if (token) {
                                  void membership.run(
                                    item.username,
                                    token,
                                    null,
                                  );
                                }
                              }}
                            >
                              <i18n.Translate>Remove</i18n.Translate>
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={membership.running || !token}
                              class="disabled:opacity-50 disabled:bg-gray-600 disabled:hover:bg-gray-600 disabled:cursor-default cursor-pointer rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                              onClick={() => {
                                if (token) {
                                  void membership.run(
                                    item.username,
                                    token,
                                    classId,
                                  );
                                }
                              }}
                            >
                              <i18n.Translate>Add</i18n.Translate>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {!userListPage.loadFirst && !userListPage.loadNext ? undefined : (
            <nav
              class="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg"
              aria-label={i18n.str`Pagination`}
            >
              <div class="flex flex-1 justify-between sm:justify-end">
                <button
                  type="button"
                  name="first page"
                  class="relative disabled:bg-gray-100 disabled:text-gray-500 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-onBackground ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0"
                  disabled={!userListPage.loadFirst}
                  onClick={userListPage.loadFirst}
                >
                  <i18n.Translate>First page</i18n.Translate>
                </button>
                <button
                  type="button"
                  name="next page"
                  class="relative disabled:bg-gray-100 disabled:text-gray-500 ml-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-onBackground ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0"
                  disabled={!userListPage.loadNext}
                  onClick={userListPage.loadNext}
                >
                  <i18n.Translate>Next</i18n.Translate>
                </button>
              </div>
            </nav>
          )}
        </div>
      </div>
    </Fragment>
  );
}
